import { Directive, OnDestroy, OnInit } from "@angular/core";
import { NotifierService } from "src/app/_services/notifier.service";
import { APIService } from "src/app/api/api.service";
import { environment } from "src/environments/environment";

@Directive()
export abstract class AzureCostOptimizationBaseComponent
  implements OnInit, OnDestroy
{
  userId: string = localStorage.getItem("selectedClientList");
  accountId: string = localStorage.getItem("accountId");
  resourceGroup: string = localStorage.getItem("selectedResourceGroup");
  selectedCloudPartner: string = localStorage.getItem("cp-selected");
  urlPrefix: string =
    localStorage.getItem("role") == "Admin" ? "admin" : "client";
  loading: boolean = false;
  fetching: boolean = true;
  currentMessage: any;
  vmOptimizations: any[] = [];
  jobDetails: any = null;
  loadStatusCount: number = 0;
  destroyed: boolean = false;
  showGlobalLoaderOnNextStatusLoad: boolean = false;

  pages: any = [10, 50, 100, 200];
  pagination: any = {
    page: 1,
    perPage: 10,
  };

  headers: any[] = [];

  abstract pageHeading: string;
  abstract recommendationSummary: string;
  protected abstract scanAction: string;

  constructor(
    protected apiService: APIService,
    public notifier: NotifierService
  ) {}

  ngOnInit(): void {
    this.accountId = localStorage.getItem("accountId");
    this.resourceGroup = localStorage.getItem("selectedResourceGroup");
    this.selectedCloudPartner = localStorage.getItem("cp-selected");

    this.currentMessage = this.notifier.currentMessage.subscribe((msg) => {
      let d = JSON.parse(msg);
      if (d.value == null) {
        return;
      }
      this.selectedCloudPartner = localStorage.getItem("cp-selected");
      if (d.key == "accountId") {
        this.accountId = d.value;
        this.refresh(true);
      } else if (d.key == "resourceGroup") {
        this.resourceGroup = d.value;
        this.refresh(true);
      }
    });

    this.refresh(true);
  }

  refresh(showGlobalLoader: boolean = false) {
    if (showGlobalLoader) {
      this.showGlobalLoaderOnNextStatusLoad = true;
    }
    if (this.selectedCloudPartner != "azure") {
      this.jobDetails = null;
      this.vmOptimizations = [];
      this.loading = false;
      this.fetching = false;
      this.showGlobalLoaderOnNextStatusLoad = false;
      return;
    }
    this.pagination.page = 1;
    this.loadStatusCount = 0;
    this.vmOptimizations = [];
    this.fetching = true;
    this.loadScanStatus();
  }

  get azureCustomAnalysisBasePath(): string {
    return `${environment.apiURL}/${this.urlPrefix}/azure/costoptimization/cost-optimization/underutilized-vms/custom-analysis`;
  }

  get authHeader(): any {
    return {
      Authorization: localStorage.getItem("t"),
    };
  }

  buildQuery(params: any) {
    return Object.keys(params)
      .map((key) => {
        const value = params[key];
        const normalizedValue =
          value === null || value === undefined ? "" : value;
        return `${key}=${encodeURIComponent(normalizedValue)}`;
      })
      .join("&");
  }

  parseApiError(result: any): string {
    if (!result) {
      return "Unknown error";
    }
    if (typeof result == "string") {
      return result;
    }
    return result.error || result.message || "Unknown error";
  }

  formatDateTime(dateString: string): string {
    if (!dateString) {
      return "-";
    }
    const dt = new Date(dateString);
    if (isNaN(dt.getTime())) {
      return dateString;
    }
    return dt.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }

  normalizeScanItem(item: any): any {
    return {
      ...item,
      scanned_date_time: item.scanned_date_time,
      scanned_date_time_label: this.formatDateTime(item.scanned_date_time),
      completed_aate_time: item.completed_aate_time || item.completed_date_time,
      completed_aate_time_label: this.formatDateTime(
        item.completed_aate_time || item.completed_date_time
      ),
    };
  }

  async loadScanStatus() {
    const showGlobalLoader = this.showGlobalLoaderOnNextStatusLoad;
    if (showGlobalLoader) {
      this.notifier.loading(true);
    }
    this.loading = true;
    try {
      const statusUrl = `${this.azureCustomAnalysisBasePath}/scan-status?${this.buildQuery(
        {
          user_id: this.userId,
          resource_group_name: this.resourceGroup,
          subscription_id: this.accountId,
          action: this.scanAction,
        }
      )}`;

      let result: any = await this.apiService.getDataPromis(
        statusUrl,
        {},
        this.authHeader
      );

      if (this.destroyed) {
        return;
      }

      if (result && result.found && result.items && result.items.length > 0) {
        const latestScan = [...result.items]
          .sort((a: any, b: any) => {
            return (
              new Date(b.scanned_date_time).getTime() -
              new Date(a.scanned_date_time).getTime()
            );
          })
          .map((scan: any) => this.normalizeScanItem(scan))[0];

        this.jobDetails = latestScan;
        if (latestScan.status == "Completed") {
          this.loadStatusCount = 0;
          await this.loadScanDetails(latestScan.scan_id);
        } else {
          this.vmOptimizations = [];
          if (this.loadStatusCount > 10) {
            this.notifier.alert(
              "Info",
              "",
              "Scan is taking longer than usual. Please click Refresh after a couple of minutes.",
              "info",
              7000
            );
            this.fetching = false;
          } else {
            const currentCount = this.loadStatusCount;
            setTimeout(() => {
              if (this.destroyed || currentCount != this.loadStatusCount) {
                return;
              }
              this.loadStatusCount += 1;
              this.loadScanStatus();
            }, (8 + this.loadStatusCount) * 1000);
          }
        }
      } else {
        this.jobDetails = null;
        this.vmOptimizations = [];
        if (result && !result.found) {
          // No scan yet; keep silent and show empty state.
        } else {
          const err = this.parseApiError(result);
          if (err.toLowerCase().indexOf("no record") == -1) {
            this.notifier.alert("Error", "", err, "error", 5000);
          }
        }
        this.fetching = false;
      }
    } finally {
      this.loading = false;
      this.showGlobalLoaderOnNextStatusLoad = false;
      if (showGlobalLoader) {
        this.notifier.loading(false);
      }
    }
  }

  async loadScanDetails(scanId: string) {
    if (!scanId) {
      this.fetching = false;
      return;
    }
    const detailsUrl = `${this.azureCustomAnalysisBasePath}/scan-details?${this.buildQuery(
      {
        user_id: this.userId,
        scan_id: scanId,
        subscription_id: this.accountId,
        action: this.scanAction,
      }
    )}`;

    let result: any = await this.apiService.getDataPromis(
      detailsUrl,
      {},
      this.authHeader
    );

    if (this.destroyed) {
      return;
    }

    if (result && (result.status == "Completed" || result.items)) {
      this.vmOptimizations = result.items || [];
      this.fetching = false;
    } else {
      this.vmOptimizations = [];
      const err = this.parseApiError(result);
      if (err.toLowerCase().indexOf("no record") == -1) {
        this.notifier.alert("Error", "", err, "error", 5000);
      }
      this.fetching = false;
    }
  }

  protected getCreateScanBasePath(): string {
    return this.azureCustomAnalysisBasePath;
  }

  protected getCreateScanParams(): any {
    return {
      user_id: this.userId,
      subscription_id: this.accountId,
      resource_group_name: this.resourceGroup,
      action: this.scanAction,
    };
  }

  async createScan(showGlobalLoader: boolean = false) {
    if (showGlobalLoader) {
      this.notifier.loading(true);
    }
    this.loading = true;
    try {
      const createScanUrl = `${this.getCreateScanBasePath()}?${this.buildQuery(
        this.getCreateScanParams()
      )}`;

      let result: any = await this.apiService.getDataPromis(
        createScanUrl,
        {},
        this.authHeader
      );
      if (result && (result.scan_id || result.status == "Scan Initiated")) {
        this.notifier.alert(
          "Success",
          "",
          result.message || "Scan initiated successfully",
          "success",
          5000
        );
        this.loadStatusCount = 0;
        this.fetching = true;
        this.vmOptimizations = [];
        this.jobDetails = this.normalizeScanItem(result);
        setTimeout(() => {
          if (!this.destroyed) {
            this.loadScanStatus();
          }
        }, 2000);
      } else {
        this.notifier.alert(
          "Error",
          "",
          this.parseApiError(result),
          "error",
          5000
        );
      }
    } catch (err: any) {
      this.notifier.alert("Error", "", err?.toString(), "error", 5000);
    } finally {
      this.loading = false;
      if (showGlobalLoader) {
        this.notifier.loading(false);
      }
    }
  }

  getTotalSavings() {
    const total = this.vmOptimizations.reduce((sum: number, vm: any) => {
      const savingsValue =
        vm.Savings ??
        vm.estimated_monthly_savings_usd ??
        vm.estimated_savings_usd ??
        0;
      return sum + Number(savingsValue || 0);
    }, 0);
    return total.toFixed(2);
  }

  getCellValue(item: any, key: string): any {
    const value = item ? item[key] : null;
    return value === null || value === undefined || value === "" ? "-" : value;
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.currentMessage) {
      this.currentMessage.unsubscribe();
    }
  }
}
